/**
 * Sample screening scenarios.
 *
 * Used in two places:
 *  - in the app (Screening → "Vzorové scénáře") to try the screening without real documents,
 *  - as fixtures in the engine tests, where `expected` defines the accepted outcome.
 *
 * The companies and people are fictional.
 */
import { Category, ContactKind } from "./types";

export interface ScreeningTemplate {
    id: string;
    /** Czech title shown in the app. */
    title: string;
    /** Czech description of what the scenario tests. */
    description: string;
    fileName: string;
    text: string;
    expected: {
        scoreMin: number;
        scoreMax: number;
        fullname?: string;
        email?: string;
        phone?: string;
        kind?: ContactKind;
        missing?: Category[];
        redFlags?: boolean;
        /** Expected error instead of a result (unreadable / empty document). */
        error?: boolean;
    };
}

export const SCREENING_TEMPLATES: ScreeningTemplate[] = [
    {
        id: "strong-company",
        title: "Stabilní výrobní firma",
        description: "Výroční zpráva zdravé firmy – růst tržeb, zisk, vysoký vlastní kapitál, certifikace. Očekávané vysoké skóre.",
        fileName: "vzor-01-stabilni-vyrobni-firma.txt",
        text: `VÝROČNÍ ZPRÁVA 2025
Název společnosti: Moravské strojírny Precise a.s.
Kontaktní osoba: Ing. Petra Horáková
E-mail: horakova@precise-strojirny.cz
Telefon: +420 603 555 210
IČO: 27845120
Typ subjektu: Společnost

1. Vize a strategie
Naší dlouhodobou vizí je stát se předním dodavatelem přesných dílů pro automobilový průmysl ve střední Evropě. Strategie na roky 2026–2028 počítá s expanzí na německý trh a s investicemi do automatizace výroby. Cílem je zvýšit tržní podíl o 15 % během 36 měsíců. Inovace řídí vlastní vývojové oddělení.

2. Finanční rozbor
Tržby za rok 2025 dosáhly 182 mil. Kč, což představuje růst tržeb o 12 % oproti předchozímu roku. Čistý zisk činil 14,5 mil. Kč a EBITDA 26 mil. Kč. Vlastní kapitál 96 mil. Kč, aktiva celkem 150 mil. Kč. Společnost je bez bankovních úvěrů a vykazuje stabilní cash flow. Plánovaná investice do nové linky je 18 mil. Kč s návratností 30 měsíců.

3. Realizace projektů
Nová linka bude spuštěna ve třech fázích podle schváleného harmonogramu s jasnými milníky: projekční příprava (8 týdnů), instalace a testování (12 týdnů), školení obsluhy (4 týdny). Projekt řídí certifikovaný projektový manažer.

4. Analýza rizik
Hlavním rizikem je kurzové riziko při exportu, které je řešeno zajišťovacími nástroji. Provozní rizika jsou kryta pojištěním. Společnost je držitelem certifikace ISO 9001 a ISO 14001. Pravděpodobnost výpadku dodávek hodnotíme jako nízkou (pravděpodobnost 5 %).

5. Provozní efektivita
Výroba je monitorována v reálném čase prostřednictvím dashboardu. Zavedení štíhlé výroby přineslo úsporu nákladů 8 %. Údržba strojů probíhá preventivně podle plánu a servis je zajištěn smluvně s SLA 24 hodin.`,
        expected: { scoreMin: 75, scoreMax: 100, fullname: "Moravské strojírny Precise a.s.", email: "horakova@precise-strojirny.cz", phone: "+420 603 555 210", kind: "Společnost", redFlags: false },
    },
    {
        id: "project-documentation",
        title: "Projektová dokumentace (TechLogix)",
        description: "Obsah vzorového PDF test_scoring.pdf – kvalitní projekt s vyčíslenou návratností a identifikovanými riziky. Očekávané střední až vyšší skóre.",
        fileName: "vzor-02-techlogix-projekt.txt",
        text: `Obsah vzorového dokumentu:  PROJEKTOVÁ DOKUMENTACE: IMPLEMENTACE SYSTÉMU SMART LOGISTICS v2.1  1. Identifikace subjektu  •   Název společnosti:   TechLogix Solutions s.r.o.  •   Kontaktní osoba:   Ing. Marek Dvořák  •   E-mail:   dvorak@techlogix - solutions.cz  •   Telefon:   +420 777 123 456  •   Typ subjektu:   Společnost  2. Vize a strategické cíle (Vision)   Naší dlouhodobou vizí je plná automatizace skladových procesů s využitím autonomních robotů. Cílem tohoto dokumentu je definovat přechod ze stávajícího manuálního systému na Smart Logistics 2.1, což by mělo zvýšit propustnost skladu o 45 % během prvních 12 měsíců. Projekt se zaměřuje na integraci AI pro predikci zásob.  3. Finanční rozbor (Finance)   Celkový rozpočet projektu je stanoven na 4.200.000 Kč bez DPH.  •   Počáteční investice (CAPEX): 2.500.000 Kč (hardware a licence).  •   Provozní náklady (OPEX): 120.000 Kč měsíčně.  •   Očekávaná návratnost (ROI): 18 měsíců díky úspoře personálních nákladů.  •   Cena implementace zahrnuje 24/7 podporu po dobu 2 let.  4. Technická implementace (Implementation)   Nasazení bude probíhat ve třech fázích:  1.   Audit stávající infrastruktury (4 týdny).  2.   Instalace senzorů a IoT bran (6 týdnů).  3.   Kalibrace AI algoritmů a školení personálu (4 týdny). Systém vyžaduje stabilní Wi -Fi 6  konektivitu a cloudové prostředí s odezvou pod 20ms.  5. Analýza rizik (Risks)  •   Kybernetická bezpečnost:   Vysoké riziko v případě slabého zabezpečení IoT senzorů. Nutné šifrování AES - 256.  •   Lidský faktor:   Možný odpor zaměstnanců k novým technologiím (řešeno motivačním programem).  •   Dodavatelský řetězec:   Riziko zpoždění dodávek čipů (pravděpodobnost 15 %).  6. Provozní aspekty (Operations)   Systém vyžaduje minimální údržbu, pouze čtvrtletní revizi hardware. Provoz bude monitorován z centrálního dashboardu, který je kompatibilní s mobilními zařízeními. Logistika svozu dat probíhá v reálném čase, což eliminuje potřebu denních manuálních inventur.`,
        expected: { scoreMin: 55, scoreMax: 80, fullname: "TechLogix Solutions s.r.o.", email: "dvorak@techlogix-solutions.cz", phone: "+420 777 123 456", kind: "Společnost", redFlags: false },
    },
    {
        id: "risky-company",
        title: "Riziková stavební firma",
        description: "Ztráta, pokles tržeb, závazky po splatnosti, exekuce a soudní spor. Očekávané nízké skóre a varování.",
        fileName: "vzor-03-rizikova-stavebni-firma.txt",
        text: `Informace o subjektu
Obchodní firma: Stavby Kolář s.r.o.
E-mail: info@stavby-kolar.cz
Telefon: 732 118 904

Vize
Společnost aktuálně nemá jasnou vizi, pokračuje v omezení činnosti a strategie není stanovena.

Finanční údaje
Tržby za rok 2025 činily 21 mil. Kč, což je pokles tržeb o 34 % proti roku 2024. Hospodaření skončilo ztrátou 6,8 mil. Kč. Vlastní kapitál 1,2 mil. Kč, aktiva celkem 38 mil. Kč. Firma eviduje závazky po splatnosti vůči dodavatelům a je financována provozním úvěrem.

Realizace
Rozpracované zakázky nemají stanovený harmonogram, termíny nejsou určeny. Firma hlásí nedostatek kapacit na stavbách.

Rizika
Proti společnosti je vedena exekuce na část majetku a probíhá soudní spor s hlavním investorem. Existuje závislost na jednom zákazníkovi (78 % tržeb). Hrozí smluvní pokuty za zpoždění. Pravděpodobnost dalšího zpoždění odhadujeme na 60 %.

Provoz
Procesy jsou neefektivní, časté prostoje techniky a výpadky dodávek materiálu.`,
        expected: { scoreMin: 0, scoreMax: 30, fullname: "Stavby Kolář s.r.o.", email: "info@stavby-kolar.cz", phone: "+420 732 118 904", kind: "Společnost", redFlags: true },
    },
    {
        id: "incomplete-lead",
        title: "Neúplná poptávka (lead)",
        description: "Krátká poptávka bez finančních údajů a harmonogramu. Očekávané nízké skóre a upozornění na chybějící podklady.",
        fileName: "vzor-04-neuplna-poptavka.txt",
        text: `Poptávka z webového formuláře
Jméno: Tomáš Beneš
E-mail: t.benes@seznam.cz
Typ subjektu: Lead

Dobrý den, naším cílem je do konce roku 2026 propojit náš e-shop se skladem a účetnictvím. Rádi bychom se dozvěděli, jak by takové řešení mohlo vypadat a co vše by bylo potřeba připravit.`,
        expected: { scoreMin: 10, scoreMax: 40, fullname: "Tomáš Beneš", email: "t.benes@seznam.cz", kind: "Lead", missing: ["finance", "risks", "operations"], redFlags: false },
    },
    {
        id: "medium-integration",
        title: "Integrace e-shopu (OSVČ)",
        description: "Menší projekt fyzické osoby s rozpočtem, dvěma fázemi a částečně ošetřenými riziky. Očekávané střední skóre.",
        fileName: "vzor-05-integrace-eshopu.txt",
        text: `Zadání projektu: integrace e-shopu
Jméno a příjmení: Jana Veselá
Typ subjektu: Fyzická osoba (OSVČ)
E-mail: jana@vesela-design.cz
Telefon: 604 222 781

Vize
Cílem je automatizace zpracování objednávek a růst online prodeje. Strategie počítá s rozšířením sortimentu.

Finanční rozbor
Rozpočet projektu je 350 000 Kč. Očekávaná návratnost 3 roky díky úspoře času při zpracování objednávek. Financováno z vlastních zdrojů.

Realizace
Integrace proběhne ve dvou fázích: napojení skladu přes API (6 týdnů) a následné testování (2 týdny).

Rizika
Riziko zpoždění dodávky od externího dodavatele e-shopu. Zálohování dat je řešeno automaticky.

Provoz
Provoz bude vyžadovat pravidelnou údržbu a podporu ze strany dodavatele.`,
        expected: { scoreMin: 45, scoreMax: 70, fullname: "Jana Veselá", email: "jana@vesela-design.cz", phone: "+420 604 222 781", kind: "Osoba", redFlags: false },
    },
    {
        id: "unreadable",
        title: "Nečitelný dokument",
        description: "Dokument téměř bez textu (např. sken bez OCR). Očekávaná chyba „nedostatek textu“.",
        fileName: "vzor-06-necitelny-dokument.txt",
        text: `Strana 1   —   ###   ~~   .`,
        expected: { scoreMin: 0, scoreMax: 0, error: true },
    },
];

/** Creates an in-memory file from a template so it can be queued like an uploaded document. */
export function templateToFile(template: ScreeningTemplate): File {
    return new File([template.text], template.fileName, { type: "text/plain" });
}
